import type { DurationUnit, I18nMessageObject } from '@_/types';
import type { DurationInput, ResolvedDurationFormatOptions } from '@formatjs/intl-durationformat';
import type { DateTime, DateTimeFormatOptions, Duration, WeekdayNumbers } from 'luxon';
import { DurationFormat } from '@formatjs/intl-durationformat';
import { getDurationSignificantUnit } from './time.util.ts';

export function getClosestLocale(
  locale: string | undefined,
  availableLocales: string[] | readonly string[]
): string | undefined {
  if (!locale) {
    return;
  }

  let localeObj: Intl.Locale;

  try {
    localeObj = new Intl.Locale(locale);
  } catch (error) {
    return;
  }

  if (availableLocales.includes(getCanonicalLocaleCode(locale))) {
    return locale;
  }

  return availableLocales.find(code => {
    const validLocale = new Intl.Locale(code);
    return (
      validLocale.baseName === localeObj.baseName || validLocale.language === localeObj.language
    );
  });
}

export function getLanguageFromLocale(locale: string): string {
  const { language } = new Intl.Locale(locale);
  return language;
}

export function getCanonicalLocaleCode(locale: string): string {
  return Intl.getCanonicalLocales(locale)[0];
}

export function getI18nValue(
  i18nObj: I18nMessageObject | undefined,
  locale: string,
  fallback?: boolean
): string {
  if (!i18nObj) {
    return '';
  }

  const canonicalLocale = getCanonicalLocaleCode(locale);

  let result = Object.entries(i18nObj).find(([loc]) => {
    const currLocale = getCanonicalLocaleCode(loc);
    return (
      currLocale === canonicalLocale ||
      getLanguageFromLocale(currLocale) === getLanguageFromLocale(canonicalLocale)
    );
  });

  if (fallback && !result?.[1]) {
    result = Object.entries(i18nObj).find(([, value]) => value);
  }

  return result?.[1] || '';
}

export function hasI18nValue(i18nObj: I18nMessageObject | undefined): boolean {
  return !!(i18nObj && Object.keys(i18nObj).find(locale => i18nObj[locale]?.trim()));
}

export function sortInLocale<T extends string>(locale: string, arr: T[]): T[];
export function sortInLocale<T>(locale: string, arr: T[], sortBy: (item: T) => string): T[];
export function sortInLocale<T>(locale: string, arr: T[], sortBy = (v: T) => v as string): T[] {
  const collator = new Intl.Collator(locale);
  return arr.sort((a, b) => collator.compare(sortBy(a), sortBy(b)));
}

export function formatDateTime(
  locale: string,
  date: DateTime,
  options?: {
    dateStyle?: 'short' | 'long' | 'numeric';
    showDate?: boolean;
    showTime?: boolean;
    showYear?: boolean;
    showMonth?: boolean;
    showDay?: boolean;
    showWeekday?: boolean;
  }
) {
  const {
    dateStyle = 'short',
    showDate = true,
    showTime = true,
    showYear = true,
    showMonth = true,
    showDay = true,
    showWeekday = true
  } = options || {};

  if (!showDate && !showTime) {
    throw new Error('showDate and showTime cannot be both false');
  }

  const formatOptions: DateTimeFormatOptions = {};
  formatOptions.year = showDate && showYear ? 'numeric' : undefined;
  formatOptions.month = showDate && showMonth ? dateStyle : undefined;
  formatOptions.day = showDate && showDay ? 'numeric' : undefined;
  if (showDate && showWeekday) {
    formatOptions.weekday = dateStyle === 'numeric' ? 'narrow' : dateStyle;
  }
  formatOptions.hour = showTime ? 'numeric' : undefined;
  formatOptions.minute = showTime ? 'numeric' : undefined;

  return new Intl.DateTimeFormat(locale, formatOptions).format(date.toJSDate());
}

export function formatDuration(
  locale: string,
  duration: Duration | [DateTime, DateTime],
  options?: {
    style?: ResolvedDurationFormatOptions['style'];
    unit?: DurationUnit | 'auto' | 'significant';
  }
) {
  const { style = 'short', unit = 'auto' } = options || {};

  const resolvedDuration = Array.isArray(duration) ? duration[1].diff(duration[0]) : duration;

  let units: DurationInput;
  if (unit === 'auto') {
    units = {
      ...resolvedDuration.shiftToAll().toObject(),
      milliseconds: undefined
    };
  } else if (unit === 'significant') {
    const significantUnit = getDurationSignificantUnit(resolvedDuration);
    units = { [significantUnit.unit]: significantUnit.value };
  } else {
    units = resolvedDuration.shiftTo(unit).toObject();
  }

  return new DurationFormat(locale, { round: true, style }).format(units);
}

export function formatCompactNumber(locale: string, value: number | string): string {
  const language = getLanguageFromLocale(locale);

  let resolvedLocale = locale;
  if (language === 'zh') {
    // Force formatter to use Chinese characters for zh-HK, otherwise it will
    // default to using English abbreviations (eg. 10K instead of 1萬)
    resolvedLocale = 'zh-Hant';
  }

  let num: number;
  if (typeof value === 'string') {
    const str = value.replace(',', '');
    if (!/^-?[0-9]+(\.[0-9]+)?$/.test(str)) {
      throw new Error(`invalid number: ${value}`);
    }
    num = parseInt(str, 10);
  } else {
    num = value;
  }

  return new Intl.NumberFormat(resolvedLocale, {
    compactDisplay: 'short',
    maximumFractionDigits: 1,
    notation: language === 'zh' && num < 10000 ? 'standard' : 'compact'
  }).format(num);
}

export function formatLanguageName(locale: string, language: string): string {
  const languageNames = new Intl.DisplayNames(locale, {
    type: 'language',
    style: 'short'
  });
  return languageNames.of(language) || '';
}

export function formatSpokenLanguage(locale: string, iso6393: string): string {
  if (iso6393.toLowerCase() === 'cmn') {
    if (getLanguageFromLocale(locale) === 'zh') {
      return '國語';
    } else if (getLanguageFromLocale(locale) === 'en') {
      return 'Mandarin';
    }
  }

  return (
    new Intl.DisplayNames([locale], {
      type: 'language',
      languageDisplay: 'dialect'
    }).of(iso6393) || ''
  );
}

export function formatWeekday(
  locale: string,
  weekday: WeekdayNumbers,
  format: DateTimeFormatOptions['weekday'] = 'long'
): string {
  return getI18nWeekDays(locale, format)[weekday - 1];
}

export function getI18nWeekDays(
  locale: string,
  format: DateTimeFormatOptions['weekday'] = 'long'
): string[] {
  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: format,
    timeZone: 'UTC'
  });
  const days = [3, 4, 5, 6, 7, 8, 9].map(day => {
    const dd = `${day}`.padStart(2, '0');
    return new Date(`2000-01-${dd}T00:00:00+00:00`);
  });
  return days.map(date => formatter.format(date));
}

export function getI18nMonths(
  locale: string,
  format: DateTimeFormatOptions['month'] = 'long'
): string[] {
  const formatter = new Intl.DateTimeFormat(locale, {
    month: format,
    timeZone: 'UTC'
  });
  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => {
    const mm = month < 10 ? `0${month}` : month;
    return new Date(`2000-${mm}-01T00:00:00+00:00`);
  });
  return months.map(date => formatter.format(date));
}

export function getCurrencySymbol(locale: string, currency: string): string {
  return (0)
    .toLocaleString(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })
    .replace(/\d/g, '')
    .trim();
}
